/**
 * Hook for session actions (clear, delete, save)
 */
import { useCallback, useState } from "react";
import type { UpdateEvent } from "../../../types/domain";
import { deleteSession as deleteSessionApi, saveSession } from "../api";
import { SENTINEL, todayISO } from "../constants";

type SessionResetState = {
  setSummary: (s: string) => void;
  setEvents: React.Dispatch<React.SetStateAction<UpdateEvent[]>>;
  setLatestSeq: (seq: number) => void;
  setPinnedSeqs: React.Dispatch<React.SetStateAction<Set<number>>>;
  setRecording: (r: boolean) => void;
  setSessionDate: (d: string) => void;
  setSimilarSessionIds: (ids: string[]) => void;
  setAssessmentIds: (ids: string[]) => void;
  setExtraNotes: (notes: string) => void;
  setLang?: (lang: "en" | "hu" | undefined) => void;
  setSessionId?: (sid: string | null) => void;
  setShowTranscript: (show: boolean) => void;
  setTranscriptMode: (mode: "history" | "live") => void;
  setLiveTranscript: (text: string) => void;
  afterSeqRef: React.MutableRefObject<number>;
  seenSeqsRef: React.MutableRefObject<Set<number>>;
  latestSeqRef: React.MutableRefObject<number>;
  summaryRef: React.MutableRefObject<{ setDraft?: (s: string) => void } | null>;
  onEffectiveSessionId?: (sid?: string) => void;
  lastPublishedSidRef: React.MutableRefObject<string | undefined>;
};

type UseSessionActionsOptions = SessionResetState & {
  API: string;
  effectiveSid: string | undefined;
  getToken: () => Promise<string>;
  setError: (msg: string | null) => void;
  setBusy: (busy: boolean) => void;
  stopTranscriptPolling: () => void;
};

/**
 * Resets all session-related state to initial values
 */
export function resetSessionState(state: SessionResetState) {
  const {
    setSummary,
    setEvents,
    setLatestSeq,
    setPinnedSeqs,
    setRecording,
    setSessionDate,
    setSimilarSessionIds,
    setAssessmentIds,
    setExtraNotes,
    setLang,
    setSessionId,
    setShowTranscript,
    setTranscriptMode,
    setLiveTranscript,
    afterSeqRef,
    seenSeqsRef,
    latestSeqRef,
    summaryRef,
    onEffectiveSessionId,
    lastPublishedSidRef,
  } = state;

  setSummary("");
  summaryRef.current?.setDraft?.("");
  setEvents([]);
  setLatestSeq(0);
  latestSeqRef.current = 0;
  setPinnedSeqs(new Set());
  setRecording(false);
  afterSeqRef.current = 0;
  seenSeqsRef.current.clear();

  setSessionDate(todayISO());
  setSimilarSessionIds([]);
  setAssessmentIds([]);
  setExtraNotes("");
  setLang?.(undefined);

  setShowTranscript(false);
  setTranscriptMode("history");
  setLiveTranscript("");

  setSessionId?.(SENTINEL);
  onEffectiveSessionId?.(undefined);
  lastPublishedSidRef.current = undefined;
}

export function useSessionActions(opts: UseSessionActionsOptions) {
  const {
    API,
    effectiveSid,
    getToken,
    setError,
    setBusy,
    stopTranscriptPolling,
    setEvents,
    setLatestSeq,
    setPinnedSeqs,
    afterSeqRef,
    seenSeqsRef,
    latestSeqRef,
    setLiveTranscript,
    setTranscriptMode,
    ...resetState
  } = opts;

  const [clearingTranscript, setClearingTranscript] = useState(false);

  const clearTranscript = useCallback(async () => {
    if (!API) {
      const msg = "Backend URL is not configured.";
      console.error("[useSessionActions] clearTranscript:", msg);
      setError(msg);
      return;
    }
    if (!effectiveSid) {
      const msg = "No session selected.";
      console.error("[useSessionActions] clearTranscript:", msg);
      setError(msg);
      return;
    }

    setClearingTranscript(true);
    setError(null);

    try {
      const token = await getToken();
      if (!token) throw new Error("Missing auth token.");

      const url = `${API}/sessions/${encodeURIComponent(effectiveSid)}/transcript/clear`;

      const resp = await fetch(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      });

      const text = await resp.text();
      let parsed: any = text;
      try {
        parsed = text ? JSON.parse(text) : {};
      } catch {}

      if (!resp.ok) throw new Error(typeof parsed === "object" ? JSON.stringify(parsed) : String(parsed || resp.status));

      setEvents([]);
      setLatestSeq(0);
      latestSeqRef.current = 0;
      setPinnedSeqs(new Set());
      afterSeqRef.current = 0;
      seenSeqsRef.current.clear();

      setLiveTranscript("");
      stopTranscriptPolling();
      setTranscriptMode("history");

      window.dispatchEvent(new Event("tj:sessions:refresh"));
    } catch (e: any) {
      console.error("[useSessionActions] clearTranscript failed:", e);
      setError(e?.message || String(e));
    } finally {
      setClearingTranscript(false);
    }
  }, [
    API,
    effectiveSid,
    getToken,
    setError,
    setEvents,
    setLatestSeq,
    setPinnedSeqs,
    stopTranscriptPolling,
    setTranscriptMode,
    setLiveTranscript,
    afterSeqRef,
    seenSeqsRef,
    latestSeqRef,
  ]);

  const deleteSession = useCallback(async () => {
    if (!effectiveSid) return;
    try {
      if (!API) throw new Error("Backend URL is not configured.");
      setBusy(true);
      setError(null);

      const token = await getToken();
      if (!token) throw new Error("Missing auth token.");

      await deleteSessionApi(
        async (url, init) => {
          const resp = await fetch(url, {
            ...init,
            headers: { ...(init?.headers || {}), Authorization: `Bearer ${token}` },
          });
          return resp;
        },
        API,
        effectiveSid
      );

      resetSessionState({
        ...resetState,
        setEvents,
        setLatestSeq,
        setPinnedSeqs,
        afterSeqRef,
        seenSeqsRef,
        latestSeqRef,
        setLiveTranscript,
        setTranscriptMode,
      });

      window.dispatchEvent(new Event("tj:sessions:refresh"));
    } catch (e: any) {
      console.error("[useSessionActions] deleteSession failed:", e);
      setError(e?.message || String(e));
    } finally {
      setBusy(false);
    }
  }, [
    API,
    effectiveSid,
    getToken,
    setBusy,
    setError,
    setEvents,
    setLatestSeq,
    setPinnedSeqs,
    afterSeqRef,
    seenSeqsRef,
    latestSeqRef,
    setLiveTranscript,
    setTranscriptMode,
    resetState,
  ]);

  return {
    clearTranscript,
    clearingTranscript,
    deleteSession,
  };
}
